import { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ArrowLeft, ChevronRight, ImagePlus, Loader2, X } from "lucide-react";

/** ALL-3: the guided "Report an issue" for customers (server: routes/help_issues.py).
 *  What it's about → what happened → which order / booking → details + photos.
 *  `preset` ({category, item_id}) starts part-filled, e.g. from an order's "Get help". */
const errText = (e) => formatApiErrorDetail(e.response?.data?.detail) || e.message;

function Choice({ label, sub, onClick, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className="w-full text-left px-4 py-3 rounded-xl border border-stone-200 hover:border-teal hover:bg-stone-50 flex items-center gap-3">
      <span className="flex-1 min-w-0"><span className="block text-sm font-medium truncate">{label}</span>{sub && <span className="block text-xs text-stone-500 truncate">{sub}</span>}</span>
      <ChevronRight className="h-4 w-4 text-stone-400 shrink-0" />
    </button>
  );
}

export default function ReportIssueForm({ preset = {}, onSent }) {
  const [guide, setGuide] = useState(null);
  const [cat, setCat] = useState(null);
  const [issue, setIssue] = useState(null);
  const [items, setItems] = useState(null);
  const [item, setItem] = useState(null);
  const [details, setDetails] = useState("");
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/help/guide").then(({ data }) => {
      setGuide(data);
      if (preset.category) setCat(data.categories.find((c) => c.key === preset.category) || null);
    }).catch((e) => toast.error(errText(e)));
  }, [preset.category]);

  useEffect(() => {
    if (!cat?.item) { setItems(null); return; }
    setItems(null);
    api.get(`/help/items?kind=${cat.item}`).then(({ data }) => {
      setItems(data);
      if (preset.item_id) { const hit = data.find((i) => i.id === preset.item_id); if (hit) setItem(hit); }
    }).catch(() => setItems([]));
  }, [cat, preset.item_id]);

  const step = !cat ? 0 : !issue ? 1 : cat.item && !item ? 2 : 3;
  const reset = () => { setCat(null); setIssue(null); setItem(null); setDetails(""); setPhotos([]); };
  const back = () => { if (step === 3 && cat.item) setItem(null); else if (step >= 2) setIssue(null); else setCat(null); };

  const addPhoto = async (file) => {
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    form.append("purpose", "issue_photo");
    setUploading(true);
    try {
      const { data } = await api.post("/uploads", form, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotos((x) => [...x, { id: data.id, name: file.name }]);
    } catch (e) { toast.error(errText(e)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const submit = async () => {
    if (details.trim().length < 5) return toast.error("Tell us a little more (at least a few words)");
    setBusy(true);
    try {
      const { data } = await api.post("/help/issues", {
        category: cat.key, issue: issue.key, item_id: item && item !== "none" ? item.id : null, details: details.trim(), photo_ids: photos.map((p) => p.id),
      });
      toast.success(`Sent. Your reference is ${data.ticket_number}.`);
      reset();
      onSent?.(data);
    } catch (e) { toast.error(errText(e)); }
    finally { setBusy(false); }
  };

  if (!guide) return <Loader2 className="h-5 w-5 animate-spin text-stone-400" />;
  return (
    <div className="space-y-3" data-testid="help-form">
      <div className="font-heading font-semibold text-lg">Report an issue</div>
      {(cat || issue) && (
        <div className="flex items-center gap-2 text-xs text-stone-600 flex-wrap">
          <button type="button" className="inline-flex items-center gap-1 text-teal hover:underline" onClick={back} data-testid="report-back"><ArrowLeft className="h-3 w-3" />Back</button>
          {cat && <Badge className="bg-stone-100 text-stone-700 border-0 rounded-full">{cat.label}</Badge>}
          {issue && <Badge className="bg-stone-100 text-stone-700 border-0 rounded-full">{issue.label}</Badge>}
          {item && item !== "none" && <Badge className="bg-teal/10 text-teal border-0 rounded-full max-w-[14rem] truncate">{item.label}</Badge>}
        </div>
      )}
      {step === 0 && (
        <div className="space-y-2">
          <p className="text-sm text-stone-600">What is the problem about?</p>
          {guide.categories.map((c) => <Choice key={c.key} label={c.label} onClick={() => setCat(c)} testid={`report-cat-${c.key}`} />)}
        </div>
      )}
      {step === 1 && (
        <div className="space-y-2">
          <p className="text-sm text-stone-600">What happened?</p>
          {cat.issues.map((i) => <Choice key={i.key} label={i.label} onClick={() => setIssue(i)} testid={`report-issue-${i.key}`} />)}
        </div>
      )}
      {step === 2 && (
        <div className="space-y-2">
          <p className="text-sm text-stone-600">{guide.item_labels[cat.item]}</p>
          {items === null ? <Loader2 className="h-4 w-4 animate-spin text-stone-400" /> : (
            <div className="space-y-2 max-h-80 overflow-auto">
              {items.map((i) => <Choice key={i.id} label={i.label} sub={i.sub} onClick={() => setItem(i)} testid={`report-item-${i.id}`} />)}
              {!items.length && <p className="text-sm text-stone-500">Nothing to show here.</p>}
            </div>
          )}
          <button type="button" className="text-xs text-stone-500 hover:underline" onClick={() => setItem("none")} data-testid="report-item-none">It's not about one of these</button>
        </div>
      )}
      {step === 3 && (
        <div className="space-y-3">
          <div>
            <Label>Tell us what happened</Label>
            <Textarea rows={5} maxLength={3000} value={details} onChange={(e) => setDetails(e.target.value)} className="mt-1" data-testid="report-details-text"
              placeholder="What went wrong, and what would you like us to do?" />
          </div>
          <div>
            <Label className="flex items-center gap-1"><ImagePlus className="h-4 w-4" /> Photos (optional, up to 5)</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {photos.map((p) => (
                <span key={p.id} className="inline-flex items-center gap-1 text-xs bg-stone-100 rounded-full px-2 py-1">
                  {p.name}<button type="button" aria-label="Remove photo" onClick={() => setPhotos((x) => x.filter((y) => y.id !== p.id))}><X className="h-3 w-3" /></button>
                </span>
              ))}
            </div>
            {photos.length < 5 && (
              <Input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" disabled={uploading} className="mt-2 h-10" data-testid="report-photo"
                onChange={(e) => addPhoto(e.target.files?.[0])} />
            )}
          </div>
          <Button type="button" onClick={submit} disabled={busy || uploading} className="w-full rounded-full bg-stoneWarm text-white hover:bg-stoneWarm/90" data-testid="report-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send to Support"}
          </Button>
        </div>
      )}
    </div>
  );
}
